import { useNuxtApp, useRuntimeConfig, useState } from '#imports'
import { ref, toRaw } from 'vue'

import { until } from '@backmarket/utils/function/until'
import { useScript } from '@unhead/vue'
import { defineStore } from 'pinia'
import { joinURL } from 'ufo'

import { useIntercomInvoker } from '../../composables/intercom/useIntercomInvoker'
import { useIntercomUserSettings } from '../../composables/intercom/useIntercomUserSettings'
import { useCareLogger } from '../../composables/logger/useCareLogger'
import type { InitType } from '../../types/intercom'
import { isClient } from '../../utils/isClient'

/**
 * Pinia store exposing the current intercom instance.
 *
 */
export const useIntercomStore = defineStore('intercom', () => {
  const { logIntercomError } = useCareLogger()
  const { callIntercomMethod, resetIntercomMethodError } = useIntercomInvoker()
  const { resolveIntercomUserSetting } = useIntercomUserSettings()
  const { INTERCOM_APP_ID } = useRuntimeConfig().public

  const intercomStoreDefaultSettings: InitType = {
    app_id: INTERCOM_APP_ID,
    api_base: 'https://api-iam.eu.intercom.io',
    hide_default_launcher: true,
    region: 'eu',
    // The Contact type attribute should be removed with CAPLA-2417
    'Contact type': 'CUSTOMER',
  }

  const isAvailable = useState('intercom-is-available', () => false)
  const intercomSettings = ref<InitType>(intercomStoreDefaultSettings)

  const removeIntercomScript = ref<(() => void) | null>(null)
  const nuxtApp = useNuxtApp()
  const loadIntercomScript = async () => {
    const { load, onError, onLoaded, remove } = useScript(
      joinURL('https://widget.intercom.io/widget', INTERCOM_APP_ID || ''),
      {
        trigger: 'manual',
        use() {
          return { Intercom: window.Intercom }
        },
      },
    )

    onLoaded(() => {
      isAvailable.value = true

      resetIntercomMethodError()
    })

    onError((error) => {
      logIntercomError({
        errorTitle: '[INTERCOM] error when loading intercom script',
        errorDetail: {
          type: 'loading script',
          context: 'The intercom script was not correctly loaded',
          error,
        },
      })
    })

    removeIntercomScript.value = remove
    await load()
  }

  async function init(
    args: Partial<InitType> &
      Partial<{
        withLoadingScript: boolean
      }> = {},
  ) {
    const argsWithDefaultValue = {
      hide_default_launcher: intercomSettings.value.hide_default_launcher,
      region: intercomSettings.value.region,
      'Contact type': intercomSettings.value['Contact type'],
      withLoadingScript: true,
      ...args,
    }

    // eslint-disable-next-line no-useless-catch
    try {
      const { withLoadingScript, ...intercomArgs } = argsWithDefaultValue

      const response = await resolveIntercomUserSetting()

      if (isClient()) {
        intercomSettings.value = {
          ...toRaw(intercomSettings.value),
          ...response,
          ...intercomArgs,
        }

        if (isAvailable.value) {
          callIntercomMethod('boot', intercomSettings.value)
          /*
           * Invokes the Intercom hide method to close any active conversations
           * during navigation between pages where Intercom is enabled.
           */
          callIntercomMethod('hide')

          return
        }

        if (withLoadingScript) {
          window.intercomSettings = toRaw(intercomSettings.value)

          nuxtApp.runWithContext(async () => {
            await loadIntercomScript()
          })
        }
      }
    } catch (error) {
      throw error
    }
  }

  async function loadIntercomOnDemand(args: Partial<InitType> = {}) {
    // eslint-disable-next-line no-useless-catch
    try {
      await init({ ...args, withLoadingScript: true })

      await until(isAvailable).toBe(true)
    } catch (error) {
      throw error
    }
  }

  function shutdown() {
    const intercomLightWeightApp = document.getElementsByClassName(
      'intercom-lightweight-app',
    )
    const intercomIframe = document.getElementById('intercom-frame')

    const intercomContainer = document.getElementById('intercom-container')

    if (removeIntercomScript.value) {
      removeIntercomScript.value()
      removeIntercomScript.value = null
    }

    if (intercomLightWeightApp[0]) intercomLightWeightApp[0].remove()

    if (intercomIframe) intercomIframe.remove()

    if (intercomContainer) intercomContainer.remove()

    if (window.Intercom) delete window.Intercom

    if (window.intercomSettings) delete window.intercomSettings

    isAvailable.value = false
    intercomSettings.value = intercomStoreDefaultSettings
  }

  return {
    isAvailable,
    init,
    loadIntercomOnDemand,
    shutdown,
  }
})
